export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export class Logger {
  private static initialized = false;
  private static level: LogLevel = 'ERROR';

  static initialize(env?: { LOG_LEVEL?: string }) {
    if (Logger.initialized) return;
    const raw = (env?.LOG_LEVEL || '').toUpperCase();
    if (raw === 'DEBUG' || raw === 'INFO' || raw === 'WARN' || raw === 'ERROR') {
      Logger.level = raw;
    } else {
      // Default to ERROR in production, DEBUG in development
      // Safely check for Vite's import.meta.env (only available in client-side)
      const isDev = typeof import.meta?.env?.DEV !== 'undefined' ? import.meta.env.DEV : false;
      Logger.level = isDev ? 'DEBUG' : 'ERROR';
    }
    Logger.initialized = true;
  }

  private static should(level: LogLevel) {
    const ranks: Record<LogLevel, number> = { DEBUG: 0, INFO: 1, WARN: 2, ERROR: 3 };
    return ranks[level] >= ranks[Logger.level];
  }

  private static log<T extends (...args: unknown[]) => void>(
    target: T,
    prefix: string,
    message?: unknown,
    ...args: unknown[]
  ) {
    if (typeof message === 'string') {
      target.call(console, `[${prefix}] ${message}`, ...args);
      return;
    }
    if (typeof message === 'undefined') {
      target.call(console, `[${prefix}]`, ...args);
      return;
    }
    target.call(console, `[${prefix}]`, message, ...args);
  }

  static debug(prefix: string, message?: unknown, ...args: unknown[]) {
    if (!Logger.should('DEBUG')) return;
    Logger.log(console.debug, prefix, message, ...args);
  }
  static info(prefix: string, message?: unknown, ...args: unknown[]) {
    if (!Logger.should('INFO')) return;
    Logger.log(console.info, prefix, message, ...args);
  }
  static warn(prefix: string, message?: unknown, ...args: unknown[]) {
    if (!Logger.should('WARN')) return;
    Logger.log(console.warn, prefix, message, ...args);
  }
  static error(prefix: string, message?: unknown, ...args: unknown[]) {
    Logger.log(console.error, prefix, message, ...args);
  }

  static getLoggerWithPrefix(prefix: string) {
    return {
      debug: (message?: unknown, ...args: unknown[]) => Logger.debug(prefix, message, ...args),
      info: (message?: unknown, ...args: unknown[]) => Logger.info(prefix, message, ...args),
      warn: (message?: unknown, ...args: unknown[]) => Logger.warn(prefix, message, ...args),
      error: (message?: unknown, ...args: unknown[]) => Logger.error(prefix, message, ...args),
    };
  }
}

